import MButton from '@assets/mui/@material-extend/MButton';
import PlusIcon from '@components/icons/Plus';
import Button from '@mui/material/Button';
import { ErrorType } from '@settings/constants';
import ErrorBoundaryStyledContainer from '@settings/ErrorBoundary/StyledComponent';
import { $error } from '@settings/errorContext';
import { getErrorTypeByMessage } from '@settings/helpers/getErrorTypeByMessage';
import { getMessageFromError } from '@settings/helpers/getMessageFromError';
import { loggerFactory } from '@settings/services/logger';
import React, { Component, ErrorInfo, FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface ErrorBoundaryState {
  hasError: boolean;
}
interface Props {
  children: React.ReactNode;
  needJSX: boolean;
}

const errorTitle: Record<ErrorType, string> = {
  shortLinkExpired: 'Common:ShortLinkExpired',
  default: 'Common:SomethingWrong',
};

const actionButton: Record<ErrorType, (props: { onClick: () => void }) => React.ReactNode> = {
  shortLinkExpired: ({ onClick }) => (
    <MButton
      className={'calculation-link'}
      onClick={onClick}
      fullWidth={true}
      variant={'outlined'}
      startIcon={<PlusIcon />}
      size={'large'}
      color={'inherit'}
    >
      <span>
        <Trans i18nKey="CartComponent:SelectedServiceButton" />
      </span>
    </MButton>
  ),
  default: ({ onClick }) => (
    <Button onClick={onClick}>
      <Trans i18nKey="Common:Reload" />
    </Button>
  ),
};

const ErrorBoundaryTitle: FC<{ back: () => void }> = ({ back }) => {
  const error = $error.getValue();
  const { t } = useTranslation('Common');

  useEffect(() => {
    localStorage.clear();
  }, []);

  const errorMessage = getMessageFromError(error);
  const foundErrorType = getErrorTypeByMessage(errorMessage) ?? 'default';

  const title = errorTitle[foundErrorType];
  const Button = actionButton[foundErrorType];

  return (
    <ErrorBoundaryStyledContainer elevation={4}>
      <h2 style={{ fontSize: '32px' }}>{t(title)}</h2>
      <Button onClick={back} />
    </ErrorBoundaryStyledContainer>
  );
};

class ErrorBoundary extends Component<Props, ErrorBoundaryState> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const logger = loggerFactory().make(`ErrorBoundary`);

    logger.Error(`Error:`, error, info.componentStack);
  }

  back = () => {
    localStorage.clear();
    history.pushState(null, '', window.location.pathname);
    $error.next(undefined);
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      // Выполните редирект на специальную страницу обработки ошибок
      // Например, используя React Router: this.props.history.push('/error-page');

      if (!this.props.needJSX) {
        return <></>;
      }

      return <ErrorBoundaryTitle back={this.back} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
